/**
 * Error Reporting User Context Sync
 *
 * Client component that syncs user authentication data to the error reporter.
 * This enables error reports to include user identification (name, email, org).
 *
 * USAGE: Include in dashboard layout after user data is fetched
 *
 * @example
 * ```tsx
 * // In dashboard layout.tsx
 * const user = await getCachedUser();
 * const { organizationId } = await getAuthContext();
 *
 * return (
 *   <GlobalProvider initialUser={user}>
 *     <ErrorReportingUserSync
 *       userId={user?.id}
 *       email={user?.email}
 *       displayName={user?.user_metadata?.full_name}
 *       avatarUrl={user?.user_metadata?.avatar_url}
 *       organizationId={organizationId}
 *     />
 *     {children}
 *   </GlobalProvider>
 * );
 * ```
 */

'use client';

import { useEffect } from 'react';
import { setUserContext, clearUserContext } from './reporter';

interface ErrorReportingUserSyncProps {
  /** Supabase user ID */
  userId?: string | null;
  /** User's email address */
  email?: string | null;
  /** User's display name (from user_metadata.full_name or name) */
  displayName?: string | null;
  /** User's avatar URL (from user_metadata.avatar_url or picture) */
  avatarUrl?: string | null;
  /** Organization ID (from JWT app_metadata or auth context) */
  organizationId?: string | null;
  /** User's role in the organization */
  role?: string | null;
}

/**
 * Syncs user authentication data to the error reporting system.
 *
 * When mounted, sets the user context for all subsequent error reports.
 * When unmounted (logout), clears the user context.
 *
 * This is a "render-nothing" component - it only handles side effects.
 */
export function ErrorReportingUserSync({
  userId,
  email,
  displayName,
  avatarUrl,
  organizationId,
  role,
}: ErrorReportingUserSyncProps) {
  useEffect(() => {
    if (userId) {
      // Set user context for error reports
      setUserContext({
        id: userId,
        email,
        displayName,
        avatarUrl,
        organizationId,
        role,
      });
    } else {
      // Clear context if no user (logged out state)
      clearUserContext();
    }

    // Cleanup on unmount (e.g., navigation away from dashboard)
    return () => {
      clearUserContext();
    };
  }, [userId, email, displayName, avatarUrl, organizationId, role]);

  // This component renders nothing - it only handles side effects
  return null;
}

export default ErrorReportingUserSync;
